import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui';

interface DatabaseColumn {
  name: string;
  type: string;
  nullable: boolean;
  defaultValue: string | null;
}

interface DatabaseRowEditorProps {
  databaseId: string;
  tableName: string;
  columns: DatabaseColumn[];
  row?: Record<string, unknown>;
  primaryKey: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  apiBasePath: string;
}

function formatCellValue(value: unknown): string {
  if (value === null) return '';
  if (value === undefined) return '';
  if (typeof value === 'object') {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return JSON.stringify(value);
  }
  return String(value);
}

export function DatabaseRowEditor({
  tableName,
  columns,
  row,
  primaryKey,
  open,
  onOpenChange,
  onSaved,
  apiBasePath,
}: DatabaseRowEditorProps) {
  const { t } = useTranslation('databases');
  const isEditing = !!row;
  const [formData, setFormData] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    columns.forEach((col) => {
      initial[col.name] = row ? formatCellValue(row[col.name]) : '';
    });
    return initial;
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);

    try {
      if (isEditing && primaryKey) {
        const primaryKeyValue = row![primaryKey];
        const data: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(formData)) {
          if (key !== primaryKey) {
            data[key] = value === '' ? null : value;
          }
        }

        const response = await fetch(
          `${apiBasePath}/tables/${encodeURIComponent(tableName)}/rows/${encodeURIComponent(String(primaryKeyValue))}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ primaryKey, data }),
          }
        );
        const result = await response.json();
        if (!response.ok) {
          setFormError(result.error || t('errors.updateRow'));
          return;
        }
      } else {
        const data: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(formData)) {
          if (value !== '') {
            data[key] = value;
          }
        }

        const response = await fetch(
          `${apiBasePath}/tables/${encodeURIComponent(tableName)}/rows`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
          }
        );
        const result = await response.json();
        if (!response.ok) {
          setFormError(result.error || t('errors.addRow'));
          return;
        }
      }

      onOpenChange(false);
      onSaved();
    } catch {
      setFormError(isEditing ? t('errors.updateRow') : t('errors.addRow'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('actions.editRow') : t('actions.addRow')}</DialogTitle>
          <DialogDescription>{t('tables.tableLabel', { table: tableName })}</DialogDescription>
        </DialogHeader>
        <div className="py-4 overflow-y-auto flex-1 space-y-4">
          {formError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
              {formError}
            </div>
          )}
          {columns.map((col) => (
            <div key={col.name}>
              <Label className="mb-1 block">
                {col.name}
                <span className="ml-2 text-xs text-muted-foreground">({col.type})</span>
                {isEditing && col.name === primaryKey && (
                  <span className="ml-2 text-xs text-warning">({t('tables.primaryKeyReadOnly')})</span>
                )}
                {!col.nullable && !isEditing && <span className="text-destructive ml-1">*</span>}
              </Label>
              <Input
                value={formData[col.name] || ''}
                onChange={(e) => setFormData({ ...formData, [col.name]: e.target.value })}
                placeholder={col.defaultValue ? t('tables.defaultValue', { value: col.defaultValue }) : col.nullable ? t('tables.null') : t('tables.required')}
                disabled={isEditing && col.name === primaryKey}
                className={isEditing && col.name === primaryKey ? 'bg-muted text-muted-foreground' : ''}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('actions.saving') : t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
